import { ProtocolCardTitle } from './chunk-63JHPR66.js';
import { ExecutionPlan } from './chunk-5FQILYCC.js';
import { UNISWAP_COMPONENTS, UNISWAP_UNIVERSAL_ROUTER, UNISWAP_PERMIT2, UNISWAP_V1_FACTORY, UNISWAP_V1_EXCHANGE, UNISWAP_V2_ROUTER, UNISWAP_V3_ROUTER, UNISWAP_V3_POSITION_MANAGER, UNISWAP_V4_POSITION_MANAGER, UNISWAP_V4_POOL_MANAGER, UNISWAP_V2_PAIR, UNISWAP_V3_POOL, ProtocolValue, decodedOrRaw, legField, UNISWAP_FIELD_LABELS, pickField, SectionCard, humanizeFieldName, FactGrid, ProtocolParamList, ProtocolBadge, UNISWAP_SWAP_COMMANDS, isRecord, pickFrom, UNISWAP_COMMAND_LABELS } from './chunk-KRDPBEWT.js';
import { decodedOf, rawOf, displayOf } from '@authe/fe-commons/storage';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { Droplets, FileSignature, ArrowLeftRight, Repeat } from 'lucide-react';
import { isUnlimitedAllowance } from '@authe/fe-commons/value';

function TokenQuantity({ value, fungible = true }) {
  const decoded = decodedOf(value);
  const hasScale = typeof decoded?.decimals === "number" && Number.isInteger(decoded.decimals) && decoded.decimals >= 0 && decoded.decimals <= 36 && (decoded.amount != null || decoded.value != null);
  const integer = (item) => (typeof item === "string" || typeof item === "bigint" || typeof item === "number") && /^(?:-?\d+|0x[\da-f]+)$/i.test(String(item));
  const unscaled = fungible && !hasScale && integer(rawOf(value)) && integer(displayOf(value));
  return /* @__PURE__ */ jsxs("span", { className: "inline-flex min-w-0 flex-wrap items-center gap-1.5", children: [
    /* @__PURE__ */ jsx(ProtocolValue, { value }),
    unscaled && /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", title: "Token decimals are unavailable for this argument. This is the exact unscaled amount.", children: "base units" })
  ] });
}
function routerRecipientLabel(value) {
  const raw = rawOf(value);
  if (raw === "0x0000000000000000000000000000000000000001") return "Router caller";
  if (raw === "0x0000000000000000000000000000000000000002") return "This router";
  return void 0;
}
function unsignedAmount(value) {
  const raw = rawOf(value);
  if (typeof raw !== "string" && typeof raw !== "number" && typeof raw !== "bigint") return void 0;
  if (!/^(?:\d+|0x[\da-f]+)$/i.test(String(raw))) return void 0;
  return BigInt(raw);
}
function routerAmountMeaning(leg, field, value) {
  const command = legName(leg), amount = unsignedAmount(value);
  if (command === "V2_SWAP_EXACT_IN" && field === "amountIn" && amount === 0n) return "Already paid to pool";
  if (amount !== 1n << 255n) return void 0;
  if (command === "V3_SWAP_EXACT_IN" && field === "amountIn" || command === "V2_SWAP_EXACT_IN" && field === "amountIn" && decodedOrRaw(legField(leg, "payerIsUser")) === false || command === "WRAP_ETH" && ["amount", "amountMin"].includes(field)) return "Entire router balance";
  const token = rawOf(legField(leg, "token"));
  if (command === "TRANSFER" && field === "value" && typeof token === "string" && /^0x[\da-f]{40}$/i.test(token) && !/^0x0{40}$/i.test(token)) return "Entire router balance";
  return void 0;
}
function v4AmountMeaning(action, field, value) {
  const amount = unsignedAmount(value);
  if (amount === void 0) return void 0;
  if (field === "amount" && action === "SETTLE") {
    if (amount === 1n << 255n) return "Entire router balance";
    if (amount === 0n) return "Full outstanding debt";
  }
  if (amount !== 0n) return void 0;
  if (field === "amount" && action === "TAKE" || field === "amountIn" && ["SWAP_EXACT_IN", "SWAP_EXACT_IN_SINGLE"].includes(action)) return "Full available credit";
  if (field === "amountOut" && ["SWAP_EXACT_OUT", "SWAP_EXACT_OUT_SINGLE"].includes(action)) return "Full outstanding debt";
  return void 0;
}
function legName(leg) {
  const name = decodedOrRaw(legField(leg, "command_name", "_command", "action_name"));
  return typeof name === "string" ? name : "";
}
function commandLegs(params) {
  const legacy = decodedOrRaw(params.find((p) => p.name === "decoded_commands")?.value);
  if (Array.isArray(legacy)) return legacy;
  const inputs = decodedOrRaw(params.find((p) => p.name === "inputs")?.value);
  return Array.isArray(inputs) ? inputs.map(decodedOrRaw) : [];
}
function pathEndpoints(path, reverse = false) {
  const arr = decodedOrRaw(path);
  if (!Array.isArray(arr) || arr.length < 2) return {};
  const tokenOf = (hop) => {
    const inner = decodedOrRaw(hop);
    return isRecord(inner) ? pickFrom(hop, "token") : hop;
  };
  const input = tokenOf(arr[0]);
  const output = tokenOf(arr[arr.length - 1]);
  if (input == null || output == null) return {};
  return reverse ? { input: output, output: input } : { input, output };
}
function commandLegFacts(leg) {
  const { input, output } = pathEndpoints(legField(leg, "path"), legName(leg) === "V3_SWAP_EXACT_OUT");
  const token = legField(leg, "token");
  const spender = legField(leg, "spender");
  const recipient = legField(leg, "recipient");
  const amountIn = legField(leg, "amountIn");
  const amountOutMin = legField(leg, "amountOutMin");
  const amountOut = legField(leg, "amountOut");
  const amountInMax = legField(leg, "amountInMax");
  const amount = legField(leg, "amount");
  const value = legField(leg, "value");
  const amountMin = legField(leg, "amountMin");
  const bips = legField(leg, "bips");
  const payerIsUser = legField(leg, "payerIsUser");
  const quantity = (field, value2) => {
    const meaning = routerAmountMeaning(leg, field, value2);
    return meaning ? /* @__PURE__ */ jsx("span", { children: meaning }) : /* @__PURE__ */ jsx(TokenQuantity, { value: value2 });
  };
  return [
    { label: UNISWAP_FIELD_LABELS.tokenIn, value: /* @__PURE__ */ jsx(ProtocolValue, { value: input }), when: input },
    { label: UNISWAP_FIELD_LABELS.tokenOut, value: /* @__PURE__ */ jsx(ProtocolValue, { value: output }), when: output },
    { label: UNISWAP_FIELD_LABELS.token, value: /* @__PURE__ */ jsx(ProtocolValue, { value: token }), when: token },
    { label: UNISWAP_FIELD_LABELS.spender, value: /* @__PURE__ */ jsx(ProtocolValue, { value: spender }), when: spender },
    { label: UNISWAP_FIELD_LABELS.recipient, value: /* @__PURE__ */ jsx(PlanRecipient, { value: recipient }), when: recipient },
    { label: UNISWAP_FIELD_LABELS.amountIn, value: quantity("amountIn", amountIn), when: amountIn != null },
    { label: UNISWAP_FIELD_LABELS.amountOutMin, value: /* @__PURE__ */ jsx(TokenQuantity, { value: amountOutMin }), when: amountOutMin != null },
    { label: UNISWAP_FIELD_LABELS.amountOut, value: /* @__PURE__ */ jsx(TokenQuantity, { value: amountOut }), when: amountOut != null },
    { label: UNISWAP_FIELD_LABELS.amountInMax, value: /* @__PURE__ */ jsx(TokenQuantity, { value: amountInMax }), when: amountInMax != null },
    { label: UNISWAP_FIELD_LABELS.amount, value: quantity("amount", amount), when: amount != null },
    { label: "Value", value: quantity("value", value), when: value != null },
    { label: legName(leg) === "WRAP_ETH" ? "Amount to wrap" : "Min amount", value: quantity("amountMin", amountMin), when: amountMin != null },
    { label: "Portion (basis points)", value: /* @__PURE__ */ jsx(ProtocolValue, { value: bips }), when: bips != null },
    { label: UNISWAP_FIELD_LABELS.payer, value: /* @__PURE__ */ jsx(ProtocolValue, { value: payerIsUser }), when: payerIsUser != null }
  ];
}
function PlanRecipient({ value }) {
  const label = routerRecipientLabel(value);
  return label ? /* @__PURE__ */ jsx("span", { children: label }) : /* @__PURE__ */ jsx(ProtocolValue, { value });
}
function V4ActionBody({ leg }) {
  const action = legName(leg);
  const tuple = legField(leg, "exactInputSingle", "exactInput", "exactOutputSingle", "exactOutput") ?? leg;
  const currency = legField(leg, "currency");
  const pool = legField(tuple, "poolKey");
  const fields = [
    ["amount", "Amount"],
    ["amountIn", "Amount in"],
    ["amountOut", "Amount out"],
    ["amountOutMinimum", "Minimum received"],
    ["amountInMaximum", "Maximum spent"],
    ["minAmount", "Minimum received"],
    ["maxAmount", "Maximum spent"]
  ];
  const facts = [
    { label: "Currency", value: /* @__PURE__ */ jsx(ProtocolValue, { value: currency }), when: currency },
    { label: "Pool key", value: /* @__PURE__ */ jsx(ProtocolValue, { value: pool }), when: pool },
    ...fields.map(([field, label]) => {
      const value = legField(tuple, field);
      const meaning = v4AmountMeaning(action, field, value);
      return { label, value: meaning ? /* @__PURE__ */ jsx("span", { children: meaning }) : /* @__PURE__ */ jsx(TokenQuantity, { value }), when: value != null };
    })
  ];
  const recipient = legField(leg, "recipient");
  if (recipient != null) facts.push({ label: UNISWAP_FIELD_LABELS.recipient, value: /* @__PURE__ */ jsx(PlanRecipient, { value: recipient }) });
  if (!facts.some((f) => !("when" in f) || Boolean(f.when))) return /* @__PURE__ */ jsx(ProtocolValue, { value: leg });
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(FactGrid, { facts }),
    /* @__PURE__ */ jsxs("details", { className: "mt-2 min-w-0", children: [
      /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-xs", children: "All action fields" }),
      /* @__PURE__ */ jsx(ProtocolValue, { value: leg })
    ] })
  ] });
}
function CommandLegBody({ leg }) {
  if (legField(leg, "action_name") != null) return /* @__PURE__ */ jsx(V4ActionBody, { leg });
  const facts = commandLegFacts(leg);
  if (facts.some((f) => "when" in f ? Boolean(f.when) : f.value != null)) {
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(FactGrid, { facts }),
      /* @__PURE__ */ jsxs("details", { className: "mt-2 min-w-0", children: [
        /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-xs", children: "All command fields" }),
        /* @__PURE__ */ jsx(ProtocolValue, { value: leg })
      ] })
    ] });
  }
  const raw = legField(leg, "raw_input", "seaportData", "tradeData", "raw_data");
  return /* @__PURE__ */ jsx(ProtocolValue, { value: raw ?? leg });
}
function UniswapLiquidity({ call: call2, hint }) {
  const pid = hint.protocolId ?? UNISWAP_V3_POSITION_MANAGER.protocolId;
  const actions = decodedOrRaw(pickField(call2.params, "decoded_actions"));
  const tokenId = pickField(call2.params, "tokenId");
  const token0 = pickField(call2.params, "token0");
  const token1 = pickField(call2.params, "token1");
  const fee = pickField(call2.params, "fee");
  const tickLower = pickField(call2.params, "tickLower");
  const tickUpper = pickField(call2.params, "tickUpper");
  const liquidity = pickField(call2.params, "liquidity", "liquidityDelta");
  const amount0 = pickField(call2.params, "amount0");
  const amount1 = pickField(call2.params, "amount1");
  const amount0Desired = pickField(call2.params, "amount0Desired");
  const amount1Desired = pickField(call2.params, "amount1Desired");
  const amount0Max = pickField(call2.params, "amount0Max");
  const amount1Max = pickField(call2.params, "amount1Max");
  const amount0Min = pickField(call2.params, "amount0Min");
  const amount1Min = pickField(call2.params, "amount1Min");
  const recipient = pickField(call2.params, "recipient", "to");
  const deadline = pickField(call2.params, "deadline");
  const facts = [
    { label: UNISWAP_FIELD_LABELS.contract, value: /* @__PURE__ */ jsx(ProtocolValue, { value: call2.address }), when: call2.address },
    { label: UNISWAP_FIELD_LABELS.tokenId, value: /* @__PURE__ */ jsx(ProtocolValue, { value: tokenId }), when: tokenId != null },
    { label: UNISWAP_FIELD_LABELS.token0, value: /* @__PURE__ */ jsx(ProtocolValue, { value: token0 }), when: token0 },
    { label: UNISWAP_FIELD_LABELS.token1, value: /* @__PURE__ */ jsx(ProtocolValue, { value: token1 }), when: token1 },
    { label: UNISWAP_FIELD_LABELS.fee, value: /* @__PURE__ */ jsx(ProtocolValue, { value: fee }), when: fee != null },
    { label: UNISWAP_FIELD_LABELS.tickLower, value: /* @__PURE__ */ jsx(ProtocolValue, { value: tickLower }), when: tickLower != null },
    { label: UNISWAP_FIELD_LABELS.tickUpper, value: /* @__PURE__ */ jsx(ProtocolValue, { value: tickUpper }), when: tickUpper != null },
    { label: UNISWAP_FIELD_LABELS.liquidity, value: /* @__PURE__ */ jsx(ProtocolValue, { value: liquidity }), when: liquidity != null },
    { label: UNISWAP_FIELD_LABELS.amount0, value: /* @__PURE__ */ jsx(TokenQuantity, { value: amount0 }), when: amount0 != null },
    { label: UNISWAP_FIELD_LABELS.amount1, value: /* @__PURE__ */ jsx(TokenQuantity, { value: amount1 }), when: amount1 != null },
    { label: "Desired amount 0", value: /* @__PURE__ */ jsx(TokenQuantity, { value: amount0Desired }), when: amount0Desired != null },
    { label: "Desired amount 1", value: /* @__PURE__ */ jsx(TokenQuantity, { value: amount1Desired }), when: amount1Desired != null },
    { label: "Maximum amount 0", value: /* @__PURE__ */ jsx(TokenQuantity, { value: amount0Max }), when: amount0Max != null },
    { label: "Maximum amount 1", value: /* @__PURE__ */ jsx(TokenQuantity, { value: amount1Max }), when: amount1Max != null },
    { label: UNISWAP_FIELD_LABELS.amount0Min, value: /* @__PURE__ */ jsx(TokenQuantity, { value: amount0Min }), when: amount0Min != null },
    { label: UNISWAP_FIELD_LABELS.amount1Min, value: /* @__PURE__ */ jsx(TokenQuantity, { value: amount1Min }), when: amount1Min != null },
    { label: UNISWAP_FIELD_LABELS.recipient, value: /* @__PURE__ */ jsx(ProtocolValue, { value: recipient }), when: recipient },
    { label: UNISWAP_FIELD_LABELS.deadline, value: /* @__PURE__ */ jsx(ProtocolValue, { value: deadline }), when: deadline != null }
  ];
  return /* @__PURE__ */ jsxs(
    SectionCard,
    {
      title: /* @__PURE__ */ jsx(ProtocolCardTitle, { protocolId: pid, icon: Droplets, label: call2.label ?? "Liquidity" }),
      protocolId: pid,
      meta: /* @__PURE__ */ jsx(ProtocolBadge, { protocolId: pid }),
      children: [
        Array.isArray(actions) && actions.length > 0 ? /* @__PURE__ */ jsx(
          ExecutionPlan,
          {
            items: actions,
            label: (leg) => humanizeFieldName(legName(leg)) || "Action",
            renderItem: (leg) => /* @__PURE__ */ jsx(CommandLegBody, { leg })
          }
        ) : /* @__PURE__ */ jsx(FactGrid, { facts }),
        /* @__PURE__ */ jsxs("details", { className: "mt-3 min-w-0", children: [
          /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-xs text-muted-foreground hover:text-foreground", children: "All call arguments" }),
          /* @__PURE__ */ jsx("div", { className: "pt-2", children: /* @__PURE__ */ jsx(ProtocolParamList, { params: call2.params, emptyLabel: "No decoded arguments" }) })
        ] })
      ]
    }
  );
}
var UNLIMITED_TITLE = "No limit \u2014 the maximum an approval can carry. The spender can move the whole balance, until the permission expires or is revoked.";
function AllowanceAmount({ value, bits }) {
  const raw = rawOf(value);
  const text = typeof raw === "string" || typeof raw === "bigint" ? String(raw) : null;
  if (text && isUnlimitedAllowance(text, bits)) {
    return /* @__PURE__ */ jsx("span", { className: "inline-flex items-center gap-1 text-sm font-medium", title: `${UNLIMITED_TITLE} (${text} base units)`, children: "Unlimited" });
  }
  return /* @__PURE__ */ jsx(TokenQuantity, { value });
}
function UniswapPermit2({ call: call2, hint }) {
  const allowance = ["approve", "permit", "permitBatch"].includes(call2.label ?? call2.signature?.split("(")[0] ?? "");
  const pid = hint.protocolId ?? UNISWAP_PERMIT2.protocolId;
  const batch = call2.signature?.includes("[]") || call2.params.some((p) => p.type?.endsWith("[]"));
  const token = pickField(call2.params, "token");
  const spender = pickField(call2.params, "spender");
  const amount = pickField(call2.params, "amount");
  const expiration = pickField(call2.params, "expiration");
  const nonce = pickField(call2.params, "nonce");
  const sigDeadline = pickField(call2.params, "sigDeadline");
  const owner = pickField(call2.params, "owner", "from");
  const to = pickField(call2.params, "to", "recipient");
  const facts = [
    { label: UNISWAP_FIELD_LABELS.contract, value: /* @__PURE__ */ jsx(ProtocolValue, { value: call2.address }), when: call2.address },
    { label: UNISWAP_FIELD_LABELS.token, value: /* @__PURE__ */ jsx(ProtocolValue, { value: token }), when: token },
    { label: UNISWAP_FIELD_LABELS.owner, value: /* @__PURE__ */ jsx(ProtocolValue, { value: owner }), when: owner },
    { label: UNISWAP_FIELD_LABELS.spender, value: /* @__PURE__ */ jsx(ProtocolValue, { value: spender }), when: spender },
    { label: UNISWAP_FIELD_LABELS.to, value: /* @__PURE__ */ jsx(ProtocolValue, { value: to }), when: to },
    { label: allowance ? "Allowance" : UNISWAP_FIELD_LABELS.amount, value: allowance ? /* @__PURE__ */ jsx(AllowanceAmount, { value: amount, bits: 160 }) : /* @__PURE__ */ jsx(TokenQuantity, { value: amount }), when: amount != null },
    { label: UNISWAP_FIELD_LABELS.expiration, value: /* @__PURE__ */ jsx(ProtocolValue, { value: expiration }), when: expiration != null },
    { label: UNISWAP_FIELD_LABELS.nonce, value: /* @__PURE__ */ jsx(ProtocolValue, { value: nonce }), when: nonce != null },
    { label: UNISWAP_FIELD_LABELS.sigDeadline, value: /* @__PURE__ */ jsx(ProtocolValue, { value: sigDeadline }), when: sigDeadline != null }
  ];
  return /* @__PURE__ */ jsxs(
    SectionCard,
    {
      title: /* @__PURE__ */ jsx(ProtocolCardTitle, { protocolId: pid, icon: FileSignature, label: call2.label ?? "Permit2" }),
      protocolId: pid,
      meta: /* @__PURE__ */ jsx(ProtocolBadge, { protocolId: pid }),
      children: [
        batch ? /* @__PURE__ */ jsx(ProtocolParamList, { params: call2.params, emptyLabel: "No decoded batch entries" }) : /* @__PURE__ */ jsx(FactGrid, { facts }),
        /* @__PURE__ */ jsxs("details", { className: "mt-3 min-w-0", children: [
          /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-xs text-muted-foreground hover:text-foreground", children: "All call arguments" }),
          /* @__PURE__ */ jsx("div", { className: "pt-2", children: /* @__PURE__ */ jsx(ProtocolParamList, { params: call2.params, emptyLabel: "No decoded arguments" }) })
        ] })
      ]
    }
  );
}
function UniswapPoolEvent({ event: event2, hint }) {
  const pid = hint.protocolId ?? UNISWAP_V3_ROUTER.protocolId;
  const sender = pickField(event2.params, "sender");
  const recipient = pickField(event2.params, "recipient", "to", "owner");
  const amount0 = pickField(event2.params, "amount0");
  const amount1 = pickField(event2.params, "amount1");
  const amount0In = pickField(event2.params, "amount0In");
  const amount1In = pickField(event2.params, "amount1In");
  const amount0Out = pickField(event2.params, "amount0Out");
  const amount1Out = pickField(event2.params, "amount1Out");
  const liquidity = pickField(event2.params, "liquidity", "amount");
  const liquidityDelta = pickField(event2.params, "liquidityDelta");
  const poolId = pickField(event2.params, "id", "poolId");
  const fee = pickField(event2.params, "fee");
  const tickSpacing = pickField(event2.params, "tickSpacing");
  const hooks = pickField(event2.params, "hooks");
  const isSwap = event2.name === "Swap";
  const callerDelta = isSwap && pid === "uniswap_v4";
  const poolDelta = isSwap && pid === "uniswap_v3_pool";
  const tickLower = pickField(event2.params, "tickLower");
  const tickUpper = pickField(event2.params, "tickUpper");
  const tick = pickField(event2.params, "tick");
  const sqrtPrice = pickField(event2.params, "sqrtPriceX96");
  const facts = [
    { label: UNISWAP_FIELD_LABELS.contract, value: /* @__PURE__ */ jsx(ProtocolValue, { value: event2.address }), when: event2.address },
    { label: "Pool ID", value: /* @__PURE__ */ jsx(ProtocolValue, { value: poolId }), when: poolId },
    { label: UNISWAP_FIELD_LABELS.sender, value: /* @__PURE__ */ jsx(ProtocolValue, { value: sender }), when: sender },
    { label: UNISWAP_FIELD_LABELS.recipient, value: /* @__PURE__ */ jsx(ProtocolValue, { value: recipient }), when: recipient },
    { label: callerDelta ? "Currency 0 delta (caller)" : poolDelta ? "Token 0 delta (pool)" : UNISWAP_FIELD_LABELS.amount0, value: /* @__PURE__ */ jsx(TokenQuantity, { value: amount0 }), when: amount0 != null },
    { label: callerDelta ? "Currency 1 delta (caller)" : poolDelta ? "Token 1 delta (pool)" : UNISWAP_FIELD_LABELS.amount1, value: /* @__PURE__ */ jsx(TokenQuantity, { value: amount1 }), when: amount1 != null },
    { label: UNISWAP_FIELD_LABELS.amount0In, value: /* @__PURE__ */ jsx(TokenQuantity, { value: amount0In }), when: amount0In != null },
    { label: UNISWAP_FIELD_LABELS.amount1In, value: /* @__PURE__ */ jsx(TokenQuantity, { value: amount1In }), when: amount1In != null },
    { label: UNISWAP_FIELD_LABELS.amount0Out, value: /* @__PURE__ */ jsx(TokenQuantity, { value: amount0Out }), when: amount0Out != null },
    { label: UNISWAP_FIELD_LABELS.amount1Out, value: /* @__PURE__ */ jsx(TokenQuantity, { value: amount1Out }), when: amount1Out != null },
    { label: UNISWAP_FIELD_LABELS.liquidity, value: /* @__PURE__ */ jsx(ProtocolValue, { value: liquidity }), when: liquidity != null },
    { label: "Liquidity change", value: /* @__PURE__ */ jsx(ProtocolValue, { value: liquidityDelta }), when: liquidityDelta != null },
    { label: UNISWAP_FIELD_LABELS.fee, value: /* @__PURE__ */ jsx(ProtocolValue, { value: fee }), when: fee != null },
    { label: "Tick spacing", value: /* @__PURE__ */ jsx(ProtocolValue, { value: tickSpacing }), when: tickSpacing != null },
    { label: UNISWAP_FIELD_LABELS.hooks, value: /* @__PURE__ */ jsx(ProtocolValue, { value: hooks }), when: hooks },
    { label: UNISWAP_FIELD_LABELS.tickLower, value: /* @__PURE__ */ jsx(ProtocolValue, { value: tickLower }), when: tickLower != null },
    { label: UNISWAP_FIELD_LABELS.tickUpper, value: /* @__PURE__ */ jsx(ProtocolValue, { value: tickUpper }), when: tickUpper != null },
    { label: UNISWAP_FIELD_LABELS.tick, value: /* @__PURE__ */ jsx(ProtocolValue, { value: tick }), when: tick != null },
    { label: "Pool sqrt price (X96)", value: /* @__PURE__ */ jsx(ProtocolValue, { value: sqrtPrice }), when: sqrtPrice != null }
  ];
  return /* @__PURE__ */ jsxs(
    SectionCard,
    {
      title: /* @__PURE__ */ jsx(ProtocolCardTitle, { protocolId: pid, icon: ArrowLeftRight, label: hint.label ?? event2.name ?? "Pool event" }),
      protocolId: pid,
      meta: /* @__PURE__ */ jsx(ProtocolBadge, { protocolId: pid }),
      children: [
        /* @__PURE__ */ jsx(FactGrid, { facts }),
        (callerDelta || poolDelta) && /* @__PURE__ */ jsx("p", { className: "mt-2 text-xs text-muted-foreground", children: callerDelta ? "Positive deltas are owed to the caller; negative deltas are owed to the pool manager." : "Positive deltas enter the pool; negative deltas leave the pool." }),
        /* @__PURE__ */ jsxs("details", { className: "mt-3 min-w-0", children: [
          /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-xs text-muted-foreground hover:text-foreground", children: "All event fields" }),
          /* @__PURE__ */ jsx("div", { className: "pt-2", children: /* @__PURE__ */ jsx(ProtocolParamList, { params: event2.params, emptyLabel: "No decoded fields" }) })
        ] })
      ]
    }
  );
}
function UniswapSwap({ call: call2, hint }) {
  const pid = hint.protocolId ?? UNISWAP_V2_ROUTER.protocolId;
  const { input, output } = pathEndpoints(pickField(call2.params, "path"), hint.call === "exactOutput");
  const tokenIn = pickField(call2.params, "tokenIn") ?? input;
  const tokenOut = pickField(call2.params, "tokenOut") ?? output;
  const amountIn = pickField(call2.params, "amountIn");
  const amountInMax = pickField(call2.params, "amountInMaximum", "amountInMax");
  const amountOut = pickField(call2.params, "amountOut");
  const amountOutMin = pickField(call2.params, "amountOutMinimum", "amountOutMin");
  const recipient = pickField(call2.params, "recipient", "to");
  const fee = pickField(call2.params, "fee");
  const deadline = pickField(call2.params, "deadline");
  const priceLimit = pickField(call2.params, "sqrtPriceLimitX96");
  const currency0 = pickField(call2.params, "currency0");
  const currency1 = pickField(call2.params, "currency1");
  const zeroForOne = pickField(call2.params, "zeroForOne");
  const amountSpecified = pickField(call2.params, "amountSpecified");
  const hooks = pickField(call2.params, "hooks");
  const swapDelta = pickField(call2.returnParams ?? [], "swapDelta", "delta");
  const facts = [
    { label: UNISWAP_FIELD_LABELS.contract, value: /* @__PURE__ */ jsx(ProtocolValue, { value: call2.address }), when: call2.address },
    { label: UNISWAP_FIELD_LABELS.tokenIn, value: /* @__PURE__ */ jsx(ProtocolValue, { value: tokenIn }), when: tokenIn },
    { label: UNISWAP_FIELD_LABELS.tokenOut, value: /* @__PURE__ */ jsx(ProtocolValue, { value: tokenOut }), when: tokenOut },
    { label: UNISWAP_FIELD_LABELS.currency0, value: /* @__PURE__ */ jsx(ProtocolValue, { value: currency0 }), when: currency0 },
    { label: UNISWAP_FIELD_LABELS.currency1, value: /* @__PURE__ */ jsx(ProtocolValue, { value: currency1 }), when: currency1 },
    { label: UNISWAP_FIELD_LABELS.zeroForOne, value: /* @__PURE__ */ jsx(ProtocolValue, { value: zeroForOne }), when: zeroForOne != null },
    { label: UNISWAP_FIELD_LABELS.amountIn, value: /* @__PURE__ */ jsx(TokenQuantity, { value: amountIn }), when: amountIn != null },
    { label: UNISWAP_FIELD_LABELS.amountSpecified, value: /* @__PURE__ */ jsx(ProtocolValue, { value: amountSpecified }), when: amountSpecified != null },
    { label: UNISWAP_FIELD_LABELS.amountInMax, value: /* @__PURE__ */ jsx(TokenQuantity, { value: amountInMax }), when: amountInMax != null },
    { label: UNISWAP_FIELD_LABELS.amountOut, value: /* @__PURE__ */ jsx(TokenQuantity, { value: amountOut }), when: amountOut != null },
    { label: UNISWAP_FIELD_LABELS.amountOutMin, value: /* @__PURE__ */ jsx(TokenQuantity, { value: amountOutMin }), when: amountOutMin != null },
    { label: UNISWAP_FIELD_LABELS.recipient, value: /* @__PURE__ */ jsx(ProtocolValue, { value: recipient }), when: recipient },
    { label: UNISWAP_FIELD_LABELS.fee, value: /* @__PURE__ */ jsx(ProtocolValue, { value: fee }), when: fee != null },
    { label: UNISWAP_FIELD_LABELS.hooks, value: /* @__PURE__ */ jsx(ProtocolValue, { value: hooks }), when: hooks },
    { label: UNISWAP_FIELD_LABELS.sqrtPriceLimit, value: /* @__PURE__ */ jsx(ProtocolValue, { value: priceLimit }), when: priceLimit != null },
    { label: UNISWAP_FIELD_LABELS.deadline, value: /* @__PURE__ */ jsx(ProtocolValue, { value: deadline }), when: deadline != null },
    { label: UNISWAP_FIELD_LABELS.swapDelta, value: /* @__PURE__ */ jsx(ProtocolValue, { value: swapDelta }), when: swapDelta != null }
  ];
  return /* @__PURE__ */ jsxs(
    SectionCard,
    {
      title: /* @__PURE__ */ jsx(ProtocolCardTitle, { protocolId: pid, icon: ArrowLeftRight, label: call2.label ?? "Swap" }),
      protocolId: pid,
      meta: /* @__PURE__ */ jsx(ProtocolBadge, { protocolId: pid }),
      children: [
        /* @__PURE__ */ jsx(FactGrid, { facts }),
        /* @__PURE__ */ jsxs("details", { className: "mt-3 min-w-0", children: [
          /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-xs text-muted-foreground hover:text-foreground", children: "All call arguments" }),
          /* @__PURE__ */ jsx("div", { className: "pt-2", children: /* @__PURE__ */ jsx(ProtocolParamList, { params: call2.params, emptyLabel: "No decoded arguments" }) })
        ] })
      ]
    }
  );
}
function commandLabel(name) {
  return UNISWAP_COMMAND_LABELS[name] ?? (name ? humanizeFieldName(name) : "Command");
}
function UniswapUniversalRouter({ call: call2, hint }) {
  const pid = hint.protocolId ?? UNISWAP_UNIVERSAL_ROUTER.protocolId;
  const legs = commandLegs(call2.params);
  const swapLegs = legs.filter((leg) => UNISWAP_SWAP_COMMANDS.has(legName(leg)));
  const hasNestedPlan = legs.some((leg) => ["V4_SWAP", "EXECUTE_SUB_PLAN"].includes(legName(leg)));
  const headline = swapLegs.length === 1 && !hasNestedPlan ? pathEndpoints(legField(swapLegs[0], "path"), legName(swapLegs[0]) === "V3_SWAP_EXACT_OUT") : {};
  const headerFacts = [
    { label: UNISWAP_FIELD_LABELS.contract, value: /* @__PURE__ */ jsx(ProtocolValue, { value: call2.address }), when: call2.address },
    { label: UNISWAP_FIELD_LABELS.tokenIn, value: /* @__PURE__ */ jsx(ProtocolValue, { value: headline.input }), when: headline.input },
    { label: UNISWAP_FIELD_LABELS.tokenOut, value: /* @__PURE__ */ jsx(ProtocolValue, { value: headline.output }), when: headline.output },
    { label: "Commands", value: /* @__PURE__ */ jsx("span", { className: "font-mono text-xs", children: legs.length }), when: legs.length > 0 }
  ];
  return /* @__PURE__ */ jsxs(
    SectionCard,
    {
      title: /* @__PURE__ */ jsx(ProtocolCardTitle, { protocolId: pid, icon: Repeat, label: call2.label ?? "execute" }),
      protocolId: pid,
      meta: /* @__PURE__ */ jsx(ProtocolBadge, { protocolId: pid }),
      children: [
        /* @__PURE__ */ jsx(FactGrid, { facts: headerFacts }),
        legs.length > 0 && /* @__PURE__ */ jsx(
          ExecutionPlan,
          {
            items: legs,
            label: (leg) => commandLabel(legName(leg)),
            renderItem: (leg) => /* @__PURE__ */ jsx(CommandLegBody, { leg })
          }
        ),
        /* @__PURE__ */ jsxs("details", { className: "mt-3 min-w-0", children: [
          /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-xs text-muted-foreground hover:text-foreground", children: "All call arguments" }),
          /* @__PURE__ */ jsx("div", { className: "pt-2", children: /* @__PURE__ */ jsx(ProtocolParamList, { params: call2.params, emptyLabel: "No decoded arguments" }) })
        ] })
      ]
    }
  );
}

// src/protocols/uniswap/index.ts
var C = UNISWAP_COMPONENTS;
var call = (protocolId, name, component) => ({
  protocolId,
  call: name,
  component,
  summaryFields: []
});
var event = (protocolId, name, component) => ({
  protocolId,
  event: name,
  component,
  summaryFields: []
});
var calls = (protocolId, names, component) => names.map((name) => call(protocolId, name, component));
var events = (protocolId, names, component) => names.map((name) => event(protocolId, name, component));
var V1_FACTORY = ["createExchange"];
var V1_SWAPS = [
  "ethToTokenSwapInput",
  "ethToTokenTransferInput",
  "ethToTokenSwapOutput",
  "ethToTokenTransferOutput",
  "tokenToEthSwapInput",
  "tokenToEthTransferInput",
  "tokenToEthSwapOutput",
  "tokenToEthTransferOutput",
  "tokenToTokenSwapInput",
  "tokenToTokenTransferInput",
  "tokenToTokenSwapOutput",
  "tokenToTokenTransferOutput",
  "tokenToExchangeSwapInput",
  "tokenToExchangeTransferInput",
  "tokenToExchangeSwapOutput",
  "tokenToExchangeTransferOutput"
];
var V1_LIQUIDITY = ["addLiquidity", "removeLiquidity"];
var V2_SWAPS = [
  "swapExactTokensForTokens",
  "swapTokensForExactTokens",
  "swapExactETHForTokens",
  "swapETHForExactTokens",
  "swapExactTokensForETH",
  "swapTokensForExactETH",
  "swapExactTokensForTokensSupportingFeeOnTransferTokens",
  "swapExactETHForTokensSupportingFeeOnTransferTokens",
  "swapExactTokensForETHSupportingFeeOnTransferTokens"
];
var V3_SWAPS = ["exactInput", "exactInputSingle", "exactOutput", "exactOutputSingle"];
var V3_POSITION_CALLS = [
  "mint",
  "increaseLiquidity",
  "decreaseLiquidity",
  "collect",
  "burn"
];
var V4_POSITION_CALLS = ["modifyLiquidities", "modifyLiquiditiesWithoutUnlock"];
var V4_POOL_SWAPS = ["swap"];
var V4_POOL_EVENTS = ["Swap", "ModifyLiquidity", "Initialize", "Donate"];
var PERMIT2_CALLS = [
  "permit",
  "permitTransferFrom",
  "approve",
  "lockdown",
  "transferFrom",
  "permitWitnessTransferFrom"
];
var UNISWAP_HINTS = {
  valueHints: [],
  eventHints: [
    ...events(UNISWAP_V4_POOL_MANAGER.protocolId, V4_POOL_EVENTS, C.poolEvent),
    ...events(UNISWAP_V2_PAIR.protocolId, ["Swap", "Mint", "Burn", "Sync"], C.poolEvent),
    ...events(UNISWAP_V3_POOL.protocolId, ["Swap", "Mint", "Burn", "Collect", "Flash", "Initialize"], C.poolEvent)
  ],
  callHints: [
    call(UNISWAP_UNIVERSAL_ROUTER.protocolId, "execute", C.universalRouter),
    ...calls(UNISWAP_PERMIT2.protocolId, PERMIT2_CALLS, C.permit2),
    call(UNISWAP_V1_FACTORY.protocolId, V1_FACTORY[0], C.swap),
    ...calls(UNISWAP_V1_EXCHANGE.protocolId, V1_SWAPS, C.swap),
    ...calls(UNISWAP_V1_EXCHANGE.protocolId, V1_LIQUIDITY, C.liquidity),
    ...calls(UNISWAP_V2_ROUTER.protocolId, V2_SWAPS, C.swap),
    ...calls(UNISWAP_V3_ROUTER.protocolId, V3_SWAPS, C.swap),
    ...calls(UNISWAP_V3_POSITION_MANAGER.protocolId, V3_POSITION_CALLS, C.liquidity),
    ...calls(UNISWAP_V4_POSITION_MANAGER.protocolId, V4_POSITION_CALLS, C.liquidity),
    ...calls(UNISWAP_V4_POOL_MANAGER.protocolId, V4_POOL_SWAPS, C.swap)
  ]
};
function registerUniswap(registry) {
  registry.registerCall(C.universalRouter, UniswapUniversalRouter);
  registry.registerCall(C.swap, UniswapSwap);
  registry.registerCall(C.liquidity, UniswapLiquidity);
  registry.registerCall(C.permit2, UniswapPermit2);
  registry.registerEvent(C.poolEvent, UniswapPoolEvent);
  registry.registerHints(UNISWAP_HINTS);
  return registry;
}

export { AllowanceAmount, TokenQuantity, UNISWAP_HINTS, UniswapLiquidity, UniswapPermit2, UniswapPoolEvent, UniswapSwap, UniswapUniversalRouter, commandLegFacts, legName, pathEndpoints, registerUniswap };
//# sourceMappingURL=chunk-27G4S6DT.js.map
//# sourceMappingURL=chunk-27G4S6DT.js.map